/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { Button, Grid, IconButton, PageGridContainer, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';

import localStorageEx from 'bundles/common/utils/localStorageEx';

import promoBarLeftImage from 'images/coursera-plus/promo_bar_left.png';
import promoBarLeftMobileImage from 'images/coursera-plus/promo_bar_left_mobile.png';
import promoBarRightImage from 'images/coursera-plus/promo_bar_right.png';
import promoBarRightMobileImage from 'images/coursera-plus/promo_bar_right_mobile.png';

import _t from 'i18n!nls/coursera-plus';

const styles = {
  pageContainer: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-200) 0;
    }
  `,
  bannerContainer: css`
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
    border-radius: 4px;
    padding: 0 !important;
    margin: var(--cds-spacing-200);
  `,
  content: css`
    text-align: center;

    ${breakpoints.down('xs')} {
      text-align: left;
    }

    padding: var(--cds-spacing-200) 0;
  `,
  leftImage: css`
    background-image: url(${promoBarLeftImage});
    background-repeat: no-repeat;
    background-size: cover;

    ${breakpoints.down('xs')} {
      background-image: url(${promoBarLeftMobileImage});
    }
  `,
  rightImage: css`
    background-image: url(${promoBarRightImage});
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      background-image: url(${promoBarRightMobileImage});
      align-items: flex-start;
      padding: var(--cds-spacing-150) var(--cds-spacing-150) 0 0;
    }
  `,
};

type PropsFromCaller = {
  content: React.ReactNode;
  storageKey: string;
  hideNotification?: boolean;
  onDismiss?: () => void;
};

const CourseraPlusNotificationBanner = ({ content, storageKey, hideNotification, onDismiss }: PropsFromCaller) => {
  const isMobile = useMediaQuery(breakpoints.down('xs'));

  const [isDismissed, setIsDismissed] = useState<boolean>(() => localStorageEx.getItem(storageKey, JSON.parse, false));

  const handleOnDismiss = useCallback<() => $TSFixMe>(() => {
    localStorageEx.setItem(storageKey, true, JSON.stringify);
    setIsDismissed(true);
    onDismiss?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  if (!isAuthenticatedUser() || isDismissed || hideNotification) {
    return null;
  }

  return (
    <PageGridContainer maxWidth="lg" css={styles.pageContainer}>
      <Grid container item xs={12} css={styles.bannerContainer} spacing={0}>
        <Grid item xs={2} sm={3} css={styles.leftImage} />
        <Grid item xs={8} sm={6} css={styles.content}>
          {content}
        </Grid>
        <Grid item xs={2} sm={3} css={styles.rightImage}>
          {isMobile ? (
            <IconButton variant="secondary" size="small" intent="close" onClick={handleOnDismiss} />
          ) : (
            <Button variant="secondary" size="small" icon={<CloseIcon size="small" />} onClick={handleOnDismiss}>
              {_t('Dismiss')}
            </Button>
          )}
        </Grid>
      </Grid>
    </PageGridContainer>
  );
};

export default CourseraPlusNotificationBanner;
